import { Dispatch, SetStateAction, useMemo, useRef, useState } from "react";
import { Alert, Button } from "reactstrap";
import { useAtomValue } from "jotai";

import useLeaveSharedRecordSync from "../../../api/api-hooks/sharedRecords/useLeaveSharedRecordSync";
import useUserSharedRecord from "../../../api/api-hooks/sharedRecords/useUserSharedRecord";
import useGlobalApps from "../../../api/api-hooks/globalApps/useGlobalApps";
import { SharedRecordsField } from "../../../types/GlobalInterfaces";
import SharedRecordSelectionType from "./SharedRecordSelectionType";
import { tenantIDAtom } from "../../../atoms/GlobalAtoms";
import AppSpinner from "../../../elements/AppSpinner";
import { AppConfirm } from "../../../utils/PopupUtil";
import ShareRecordPage from "./SharedRecordPage";
import SharedRecord from "./SharedRecord";
import AppDrawer from "../AppDrawer";

interface Props {
  open: boolean;
  onClose: () => void;
  record: Record<string, any>;
  recordModule: string;
  globalAppID?: string;
  viewID?: string;
}

interface Selector {
  page: "Selector" | "Share_Record" | "Share_Record_Page";
  setPage: Dispatch<
    SetStateAction<"Selector" | "Share_Record" | "Share_Record_Page">
  >;
}

const ShareRecordDrawer = ({
  open,
  onClose,
  record,
  recordModule,
  globalAppID,
  viewID,
}: Props) => {
  const { globalApps, loadingApps } = useGlobalApps(!globalAppID);
  const sharedRecordRef = useRef<any>();
  const tenantID = useAtomValue(tenantIDAtom);
  const [disbaled, setDisabled] = useState(false);
  const [page, setPage] = useState<
    "Selector" | "Share_Record" | "Share_Record_Page"
  >("Selector");
  const recordID: string = useMemo(
    () =>
      record?._RemoteSystems?.find((rs: any) => !!rs.RemoteRecordID)
        ?.RemoteRecordID ?? record?.id,
    [record?.id]
  );
  const { sharedRecord, expired, sharedRecordLoading } = useUserSharedRecord({
    recordID: recordID ?? "",
  });

  let Body: React.ReactNode;

  const handleOnClose = () => {
    setPage("Selector");
    onClose();
  };

  if (loadingApps) {
    Body = <DrawerLoader />;
  } else if (page === "Selector") {
    const notGlobalApp =
      !globalAppID ||
      !globalApps?.length ||
      !globalApps.find((app: any) => app._id === globalAppID);

    Body = (
      <SharedRecordSelectionType
        setPage={setPage}
        notGlobalApp={notGlobalApp}
      />
    );
    // TODO: Add permissions
  } else if (false) {
    Body = (
      <Alert className="mt-4" color="danger">
        You don't have permission to share this Record. You must be a Cloud
        Architect or higher!
      </Alert>
    );
  } else {
    Body = (
      <ShareRecordSection
        record={record}
        recordModule={recordModule}
        globalAppID={globalAppID}
        onClose={handleOnClose}
        page={page}
        setPage={setPage}
        sharedRecordRef={sharedRecordRef}
        setDisabled={setDisabled}
        sharedRecord={sharedRecord}
        expired={expired}
        sharedRecordLoading={sharedRecordLoading}
        viewID={viewID}
      />
    );
  }

  return (
    <AppDrawer
      open={open}
      onClose={handleOnClose}
      body={Body}
      headerTitle={page === "Share_Record_Page" ? "Share Page" : "Share Record"}
      bodyCSS="mt-0 pt-0"
      headerOptions={
        <>
          {page !== "Selector" && (
            <Button
              size="sm"
              onClick={() => setPage("Selector")}
              style={{ width: 80 }}
            >
              Go Back
            </Button>
          )}
          {page === "Share_Record" &&
            sharedRecord &&
            tenantID === sharedRecord.CloudID && (
              <Button
                style={{ width: 80 }}
                size="sm"
                color="primary"
                onClick={() => sharedRecordRef?.current()}
                disabled={disbaled}
              >
                Update
              </Button>
            )}
        </>
      }
    />
  );
};

function ShareRecordSection({
  record,
  recordModule,
  globalAppID,
  page,
  sharedRecordRef,
  setDisabled,
  sharedRecord,
  expired,
  sharedRecordLoading,
  viewID,
}: Omit<
  Props &
    Selector & {
      sharedRecordRef: any;
      setDisabled: any;
      sharedRecord: SharedRecordsField;
      expired: boolean;
      sharedRecordLoading: boolean;
    },
  "open"
>) {
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const tenantID = useAtomValue(tenantIDAtom);
  const { leaveSync, leavingSync } = useLeaveSharedRecordSync();

  const sharedRecordMetadata = useMemo(() => {
    if (Array.isArray(record?._RemoteSystems) && !sharedRecord?._id)
      return record?._RemoteSystems.find((rs: any) => !!rs.SubViewRecord);

    if (!Array.isArray(record?._RemoteSystems) || !sharedRecord?._id)
      return null;

    return record?._RemoteSystems.find(
      (r: any) => r?.RemoteID === sharedRecord._id
    );
  }, [record?._RemoteSystems, sharedRecord?._id]);

  if (sharedRecordMetadata && !!sharedRecordMetadata?.SubViewRecord)
    return (
      <>
        <Alert color="warning" className="mt-4">
          This record was added from SubView. It can't be shared.
        </Alert>
      </>
    );

  if (sharedRecordLoading) return <DrawerLoader />;

  if (sharedRecord && sharedRecord.CloudID !== tenantID) {
    return (
      <>
        <Alert color="warning" className="mt-4">
          This record was added from another Cloud. It can only be shared by the
          origin Cloud.
        </Alert>
        {sharedRecordMetadata?.RemoteSyncType === "Sync" && (
          <>
            <h4 className="mb-0">Record Sync</h4>
            <small>
              This record is synced across multiple clouds. Any modifications
              you apply will be reflected in the corresponding records on these
              other clouds, and conversely, any changes made on those clouds
              will be received here. To disable this synchronization and create
              an independent copy of this record, click the 'Leave Sync' button
              below.
            </small>
            <div className="d-flex justify-content-end mt-3">
              <Button
                color="danger"
                onClick={handleLeaveSync}
                disabled={leavingSync}
              >
                {leavingSync ? "Leaving Sync..." : "Leave Sync"}
              </Button>
            </div>
          </>
        )}
        {success && <Alert color="primary">{success}</Alert>}
        {error && <Alert color="danger">{error}</Alert>}
      </>
    );
  }

  return (
    <div className="pt-0 pb-6">
      {page === "Share_Record" && (
        <SharedRecord
          recordID={record?.id}
          recordModule={recordModule}
          globalAppID={globalAppID}
          sharedRecord={sharedRecord}
          expired={expired}
          sharedRecordRef={sharedRecordRef}
          setDisabled={setDisabled}
          fromSubView={!!sharedRecordMetadata?.SubViewRecord}
        />
      )}
      {page === "Share_Record_Page" && (
        <ShareRecordPage
          recordID={record?.id}
          recordModule={recordModule}
          viewID={viewID}
        />
      )}
    </div>
  );

  async function handleLeaveSync() {
    if (!record?.id || !sharedRecord.ModuleAPIName || !sharedRecord._id) return;

    const result = await AppConfirm({
      title: "Leave Sync?",
      text: "You won't receive further updates for this record. To resume updates, you must re-add the shared record using a shared link. Note that the link may no longer exist, requiring you to contact the original owner for a new one.",
      confirmButtonText: "Leave",
    });

    if (!result.isConfirmed) return;

    leaveSync({
      recordID: record?.id,
      sharedRecordID: sharedRecord._id,
      moduleAPIName: sharedRecord.ModuleAPIName,
      onSuccess: () => setSuccess("Successfully left shared record sync."),
      onError: (err) => setError(err),
    });
  }
}

export function DrawerLoader() {
  return (
    <div className="h-100 d-flex justify-content-center align-items-center">
      <AppSpinner size="lg" />
    </div>
  );
}

export default ShareRecordDrawer;
